import React from 'react';
import PropTypes from 'prop-types';
import choiceSequenceHandler from '../../../qti/utils/ChoiceSequenceHandler';
import FeedbackForAlternative from './FeedbackForAlternative';
import sessionReader from '../../../store/readHelpers/SessionReader';
import settingsReader from '../../../store/readHelpers/SettingsReader';
import MathDisplay from '../mathDisplay/MathDisplay';

import styles from '../QuestionDisplay.scss';
import FeedbackForResponseOutcome from './FeedbackForResponseOutcome';

class FeedbackDisplay extends React.PureComponent {
    componentDidMount() {
        this.updateContent();
    }

    componentDidUpdate() {
        this.updateContent();
    }

    updateContent() {
        if (this.rootElement) {
            return MathDisplay
                .showMath(this.rootElement)
                .catch(error => window.console.log(error));
        }
        return null;
    }

    getAlternatives = (model, choiceSequence) => {
        const alternatives = model.alternatives;
        if (choiceSequence !== undefined) {
            choiceSequenceHandler.sortAlternativesAccordingToChoiceSequence(alternatives, choiceSequence);
        }
        return alternatives;
    };

    isElementSelected = (interactionId, choice) => {
        return !!(this.props.userResponses[interactionId] && this.props.userResponses[interactionId].includes(choice));
    };

    isElementCorrect = (interactionId, choice) => {
        const correctResponsesForInteraction = this.resolveCorrectResponsesForInteraction(interactionId);
        return !!(correctResponsesForInteraction && correctResponsesForInteraction.includes(choice));
    };

    /**
     * Resolving the correctResponses for an interaction as long as we have access to them AND we are allowed to use them.
     * Notably, the planner in a "feedback preview" does have access to the CR, however he should only be able to use them
     * if the AR setting allows for it. That way, we better mirror how it will be displayed to the student.
     * @param interactionId
     * @returns {Array or undefined}
     */
    resolveCorrectResponsesForInteraction(interactionId) {
        const isPreviewWithFeedback = sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false);
        if (this.props.correctResponsesForQuestion
            && (!isPreviewWithFeedback || settingsReader.isShowResultsAndCorrectAnswersForStudentReviewEnabled())) {
            return this.props.correctResponsesForQuestion[interactionId];
        }
        return undefined;
    }

    /**
     * Render container for feedback information for all alternatives in an interaction
     * @param interactionModel
     * @returns {XML}
     */
    renderFeedbackForAlternatives = (interactionModel) => {
        const { question, choiceSequences, services } = this.props;
        const { identifier } = interactionModel;
        const keyForInteractionFeedback = `feedback-${question.contentRevisionId}-${identifier}`;
        return (<div
            key={keyForInteractionFeedback}
            className={`${styles.feedbackForInteraction}`}
        >
            {this.getAlternatives(interactionModel, choiceSequences[identifier]).map((alternative) => {
                const feedback = this.props.feedback[alternative.identifier];
                const key = `feedback-${question.contentRevisionId}-${identifier}-${alternative.identifier}`;
                return (<FeedbackForAlternative
                    key={key}
                    model={alternative}
                    interaction={interactionModel}
                    question={question}
                    checked={this.isElementSelected(identifier, alternative.identifier)}
                    feedback={feedback}
                    isCorrect={this.isElementCorrect(identifier, alternative.identifier)}
                    correctResponsesForInteraction={this.resolveCorrectResponsesForInteraction(identifier)}
                    services={services}
                />);
            })}
        </div>);
    };

    renderFeedbackForNumericSimulation = (interactionModel, index) => {
        const { identifier } = interactionModel;
        const feedback = this.props.feedback?.[identifier]?.[0];

        if (!feedback) {
            return null;
        }

        const keyForInteractionFeedback = `feedback-${interactionModel.contentRevisionId}-${identifier}`;
        return (<div key={keyForInteractionFeedback}>
            <FeedbackForResponseOutcome
                {...this.props}
                feedback={feedback}
                correctResponsesForInteraction={this.resolveCorrectResponsesForInteraction(identifier)}
                identifier={identifier}
                index={index}
            />
        </div>);
    }

    render() {
        const { question, services: { feedbackService } } = this.props;

        return (
            <div
                className={`${styles.feedbackForQuestion}`}
                ref={(element) => {
                    this.rootElement = element;
                }}
            >
                <div className="question-margin left" />
                <div className={`${styles.feedbackListBody}`}>
                    {
                        question.responseModels.map((model, index) => {
                            const { identifier } = model;
                            const interactionModel = question.responseModels.filter(declaration => declaration.identifier === identifier)[0];
                            const { interactionType, type } = interactionModel;

                            if (feedbackService.showInteractionFeedback(interactionType)) {
                                return this.renderFeedbackForNumericSimulation(interactionModel, index);
                            }
                            if (feedbackService.showAlternativeFeedback(type)) {
                                return this.renderFeedbackForAlternatives(interactionModel);
                            }

                            return null;
                        })
                    }
                </div>
                <div className="question-margin right" />
            </div>
        );
    }
}

FeedbackDisplay.propTypes = {
    question: PropTypes.object,
    userResponses: PropTypes.object,
    choiceSequences: PropTypes.object,
    feedback: PropTypes.object, // feedback for this question,
    correctResponsesForQuestion: PropTypes.object,
    services: PropTypes.object,
};

export default FeedbackDisplay;
